<script lang="ts">
  import PageSection from '@athena-lib/Page/PageSection.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { HeadingSmall, HeadingXXSmall } from '@athena-lib/Text';
  import { Add, ArrowForward } from '@athena-lib/Icons';
  import Columns from '@athena-lib/Columns/Columns.svelte';
  import { Column } from '@athena-lib/Columns';
  import HeadingXSmall from '@athena-lib/Text/HeadingXSmall/HeadingXSmall.svelte';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Button } from '@athena-lib/Button';
  import Checkbox from '@athena-lib/Checkbox/Checkbox.svelte';
  import { Type } from '@sinclair/typebox';
  import Avatar from './Avatar.svelte';
  import AddressDisplay from './AddressDisplay.svelte';
  import AccountTabs from './AccountTabs.svelte';
  import Box from './Box.svelte';
  import type { CustomerData } from '$lib/server/external/types';
  import { get } from '$lib/http';

  async function setSubscribed(subscribed: boolean) {
    return get(
      '/account/subscribe_to_marketing_emails/' + (subscribed ? '0' : '1'),
      Type.Object({ success: Type.Literal(true) }),
    );
  }
  const translations = getCommonTranslations();

  const { data }: { data: CustomerData } = $props();

  let { userInfo, shipping, billing, taxExempt, taxCertificates, avatarUrl, authorInfo } = $derived(
    data.accountData,
  );

  let subscribed = $state(data.accountData.subscribed);
  const someFalsy = (obj: object) => Object.values(obj).some((v) => !v);
  const allFalsy = (obj: object) => Object.values(obj).every((v) => !v);

  let shippingIsEmpty = $derived(allFalsy(shipping));
  let ShippingIcon = $derived(shippingIsEmpty ? Add : ArrowForward);
  let billingIsEmpty = $derived(allFalsy(billing));
  let BillingIcon = $derived(billingIsEmpty ? Add : ArrowForward);
  let noPaypalTokens = $derived(someFalsy(authorInfo));

  function toggleChange() {
    setSubscribed(subscribed).catch();
  }
</script>

<AccountTabs currentPage="customer" market={data.market} accountData={data.accountData}
></AccountTabs>
<PageSection class="h-full grow" backgroundColor="medium">
  <HeadingSmall>{translations['accountProfilePageTitle']}</HeadingSmall>
  <hr class="mb-4 mt-3" />
  <Columns collapseBelow="md">
    <Column>
      <Box>
        <Stack>
          <StackItem>
            <HeadingXSmall size="xsmall">{translations['accountProfileSectionTitle']}</HeadingXSmall
            >
          </StackItem>
          <StackItem>
            <Columns space={{ sm: 'gutter', lg: 'small' }} collapseBelow="lg">
              <Column class="mr-4 w-fit min-w-fit ">
                <Avatar src={avatarUrl} hideButton />
              </Column>
              <Column>
                <section class="bs5-mt-1 bs5-mt-lg-0 bs5-ms-0 bs5-ms-lg-3">
                  <HeadingXXSmall
                    class="text-st-dark-gray sm:text-st-dark-gray md:text-st-dark-gray lg:text-st-dark-gray"
                  >
                    {billing.first_name ?? ''}
                    {billing.surname ?? ''}
                  </HeadingXXSmall>
                  <div>
                    {billing.phone ?? ''}
                  </div>
                  <div>
                    {userInfo.email}
                  </div>
                </section>
              </Column>
            </Columns>
          </StackItem>
          <StackItem>
            <hr />
          </StackItem>
          <StackItem>
            <HeadingXSmall class="mb-3">{translations['billingAndShipping']}</HeadingXSmall>
            {#if billingIsEmpty}
              <div class="flex items-center justify-center text-3xl text-st-gray">
                {translations['accountAddNew']}
              </div>
            {:else}
              <AddressDisplay address={billing} />
              <div class="self-end">
                {#if billing.is_company}
                  {#if (billing.country_code === 'se' || billing.country_code === 'no') && !!billing.company_org_number}
                    <div>
                      Org. nr {billing.company_org_number}
                    </div>
                  {:else if billing.company_vat_number}
                    <div>
                      VAT nr {billing.company_vat_number}
                    </div>
                  {/if}
                  {#if billing.country_code === 'us' && taxCertificates !== null}
                    <h6>
                      Tax Exemption:
                      <div class="tax-status-icon" class:valid={taxExempt}></div>
                      <span class="tax-status-text">{taxExempt ? 'Valid' : 'Invalid'}</span>
                    </h6>
                  {/if}
                {/if}
              </div>
            {/if}
          </StackItem>
          {#if userInfo.is_author}
            <StackItem>
              <HeadingXXSmall class="mb-2">Paypal Account</HeadingXXSmall>
              {#if noPaypalTokens}
                <Button
                  size="small"
                  element="a"
                  link={{
                    href: `/customer/paypalauthenticate?userId=${userInfo.user_id}&nonce=${userInfo.nonce}`,
                  }}>{translations['authenticatePaypal']}</Button
                >
              {:else}
                <Button
                  size="small"
                  element="a"
                  link={{
                    href: '/customer/paypallogout',
                  }}>{translations['unlinkPaypal']}</Button
                >
              {/if}
            </StackItem>
          {/if}
        </Stack>

        <Button
          element="a"
          size="content"
          link={{ href: '/customer/edit' }}
          class="absolute right-4 top-4 h-8 w-8 p-1"
        >
          <BillingIcon></BillingIcon>
        </Button>
      </Box>
    </Column>
    <Column>
      <Stack class="h-full">
        <StackItem class="flex-grow">
          <Box>
            <Stack>
              <StackItem>
                <HeadingXXSmall>{translations['accountAlternativeShippingAddress']}</HeadingXXSmall>
              </StackItem>
              <StackItem class="h-fill grow">
                {#if shippingIsEmpty}
                  <div class="flex items-center justify-center text-3xl text-st-gray">
                    {translations['accountAddNew']}
                  </div>
                {:else}
                  <AddressDisplay address={shipping} />
                {/if}

                <Button
                  element="a"
                  size="content"
                  link={{ href: '/customer/shipping' }}
                  class="absolute right-4 top-4 h-8 w-8 p-1"
                >
                  <ShippingIcon></ShippingIcon>
                </Button>
              </StackItem>
            </Stack>
          </Box>
        </StackItem>
        <StackItem>
          <Box>
            <Stack>
              <StackItem>
                <HeadingXXSmall>{translations['accountSubscription']}</HeadingXXSmall>
              </StackItem>
              <StackItem>
                <Checkbox
                  id="newsletter-checkbox"
                  checked={subscribed}
                  onClick={toggleChange}
                  disabled={false}
                  label={translations['accountNewsletterDescription']}
                />
              </StackItem>
            </Stack>
          </Box>
        </StackItem>
      </Stack>
    </Column>
  </Columns>
</PageSection>
